// frontend/src/hooks/useReportActionMutations.js
//
// Bulk action mutations triggered from the report grids' "Create X from
// selected rows" buttons. Each is a single-shot POST to a dedicated
// reports/* action endpoint, returns a structured result object that
// the report page renders inline.
//
// Failure path goes through `translateRequestError(err, t, key)`: detail in
// the console, translated `apiErrors.<code>` or fallback in the UI.

import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';


/**
 * Stockout report → "Create PO recommendations from selected rows".
 * The server plans the selected item-locations with the PO engine and
 * merges the result into the pending recommendations.
 *
 * Variables: { items } — `[{item_location_id}, ...]`
 */
export function useCreateRecommendationsFromStockout() {
  return useApiMutation({
    mutationFn: ({ items }) =>
      apiJson.post('/api/reports/stockout-items/create-recommendations/', { items }),
    changes: [DATA.recommendations],
  });
}


/**
 * Dead-stock report → "Create promotion from selected items".
 * Backend creates a draft promotion containing the rows; the merchant
 * then sets prices on the Promotions page and activates it.
 *
 * Variables: { items } — `[{item_id, location_id, item_code}, ...]`
 */
export function useCreatePromotionFromDeadStock() {
  return useApiMutation({
    mutationFn: ({ items }) =>
      apiJson.post('/api/reports/dead-stock/create-promotion/', { items }),
    changes: [DATA.promotions],
  });
}


/**
 * Dead-stock report → "Mark selected items as new product introduction".
 * The NPI flag hides them from the dead-stock report until it expires
 * (`Item.NPI_EXPIRY_DAYS`).
 *
 * Variables: { itemIds } — `[itemId, itemId, ...]` (deduped at the call site)
 */
export function useMarkItemsNPI() {
  return useApiMutation({
    mutationFn: ({ itemIds }) =>
      apiJson.post('/api/reports/dead-stock/mark-npi/', { item_ids: itemIds }),
    changes: [DATA.items],
  });
}


/**
 * Excess-stock report → "Create promotion from selected items".
 * Same shape as the dead-stock create-promotion variant but a separate
 * endpoint — backend keeps them split for audit-trail clarity (excess
 * vs. dead has different selection criteria + different downstream
 * recommendation semantics).
 *
 * Variables: { items } — `[{item_id, location_id, item_code}, ...]`
 */
export function useCreatePromotionFromExcess() {
  return useApiMutation({
    mutationFn: ({ items }) =>
      apiJson.post('/api/reports/excess-items/create-promotion/', { items }),
    changes: [DATA.promotions],
  });
}
